//Library Imports
import { Typography } from 'antd';
import { FC, useEffect, useMemo, useState } from 'react';
import store, { RootState } from 'store';
import { useSelector } from 'react-redux';
import React from 'react';

//Local Imports
import TableSort from 'modules/Common/Components/TableSort';
import { getDeliveredContactsList, setDeliveredContactsSorting } from './action';
import Speed from '../../../assets/img/icons/Speed.svg';
import { CONTACT_ANALYTICS } from 'constants/types';
import { getPagination, RedirectToContactDetails } from './utils';
import { setSortDirection } from 'modules/Analytics/utils';
import ContactLoader from './Loaders/ContactLoader';
import BlankProfilePhoto from 'assets/img/blank_profile_photo.png';

//CSS Imports
import styled from 'styled-components';
import { addCommaToNumber, convertSnakeCaseKeysToCamelCase } from 'utils';
import ContactEmailDropdownSentEmail from '../Content/ContactEmailDropdownContactEmail';
import { relative } from 'path';
interface IDeliveredContactsList {
  page?: string;
  tab?: string;
  searchedText: string;
  setSearchedText: (value: string) => void;
  accountId: number;
}

const DeliveredContactsList: FC<IDeliveredContactsList> = (props: IDeliveredContactsList) => {
  //props constants
  const { searchedText, setSearchedText, accountId } = props;

  //Local States
  const [sortType, setSortType] = useState({ accessor: '', sortDirection: 'none' });
  const [sortBy, setSortBy] = useState('');

  //Redux states
  const delivered = useSelector((state: RootState) => state.contactAnalytics.delivered);
  const loading = useSelector((state: RootState) => state.contactAnalytics.deliveredLoading);
  const total_delivered = useSelector((state: RootState) => state.contactAnalytics.total_delivered);
  const contactsByFilter = useSelector((state: RootState) => state.contactAnalytics.contactsByFilter);
  const deliveredSorting = useSelector((state: RootState) => state.contactAnalytics.deliveredSorting);

  const AllCampaignsFilterCheck = useSelector((state: RootState) => state.analyticsDashboard.appliedCampaignsFilters.isAllCampaigns);

  const { Text, Paragraph } = Typography;
  const [ellipsis, setEllipsis] = React.useState(true);

  const clickedByDropdownSendEmail = (emails_data: any) => {
    return (
      <div className={`dropdown-selecter-email active`}>
        <div className={`dropdown-options show`}>
          <div className="dropdown-options-inner">
            <div className={`dropdown-wrapper`}>
              <div className={`dropdown-top-filters body-dropdown`}>
                <span
                  onClick={(e: any) => {
                    e.stopPropagation();
                  }}
                >
                  <ContactEmailDropdownSentEmail
                    articleId={0}
                    emailsData={emails_data.map((content: any) => convertSnakeCaseKeysToCamelCase(content))}
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    setSortType(deliveredSorting);
  }, [deliveredSorting]);

  let columnsData = useMemo(
    () => [
      {
        id: 'main',
        hideHeader: true,
        disableSortBy: true,
        columns: [
          {
            id: 'contact_name',
            Header: 'Contact',
            accessor: 'contact_name',
            canSort: true,
            sortType: 'basic',
            sortDirection: sortType.accessor === 'contact_name' ? sortType.sortDirection : 'none',
            Cell: (props: any) => {
              let item = props.row.original;
              let name =
                item?.contact_name === null || (item.contact_email !== null && item.contact_name?.trim() === '')
                  ? item.contact_email
                  : item.contact_name;

              return (
                <div>
                  <div className="preview">
                    <img
                      style={{
                        boxShadow: item.personality.color ? `0px 0px 0px 2px ${item.personality.color}` : '',
                      }}
                      src={item.contact_image !== null && item.contact_image !== '' ? item.contact_image : BlankProfilePhoto}
                      alt=""
                      onError={(e: any) => {
                        e.target.onerror = null;
                      }}
                    />
                  </div>
                  <div className="details delivered-contact-list-details">
                    <>
                      <div className="name">
                        <Text className="analytics-ellipse-name" ellipsis={ellipsis ? { tooltip: `${name}` } : false}>
                          {name}
                        </Text>
                      </div>
                      <div className="company">
                        <Text className="analytics-ellipse-company" ellipsis={ellipsis ? { tooltip: `${item.contact_email}` } : false}>
                          {item.contact_email}
                        </Text>
                      </div>
                      <div className="designation">
                        <Text className="analytics-ellipse-designation" ellipsis={ellipsis ? { tooltip: `${item.job_title}` } : false}>
                          {item.job_title}
                        </Text>
                      </div>
                    </>
                  </div>
                </div>
              );
            },
          },
          {
            id: 'company_name',
            Header: 'Company',
            accessor: 'company_name',
            canSort: true,
            sortType: 'basic',
            sortDirection: sortType.accessor === 'company_name' ? sortType.sortDirection : 'none',
            Cell: (props: any) => {
              let item = props.row.original;
              return (
                <div className="company-name">
                  <Text className="analytics-ellipse-company-name" ellipsis={ellipsis ? { tooltip: `${item.company_name}` } : false}>
                    {item.company_name}
                  </Text>
                </div>
              );
            },
          },
          {
            id: 'personality',
            Header: 'Personality',
            disableSortBy: true,
            accessor: 'personality.archetype',
            Cell: (props: any) => {
              let item = props.row.original;
              return (
                <span className="organization d-flex align-items-center">
                  <Circle color={item.personality.color ? item.personality.color : 'black'} />
                  <Text
                    className="analytics-ellipse-personality"
                    ellipsis={ellipsis ? { tooltip: `${item.personality.archetype === undefined ? 'N/A' : item.personality.archetype}` } : false}
                  >
                    {item.personality.archetype === undefined ? 'N/A' : item.personality.archetype}
                  </Text>
                </span>
              );
            },
          },
          {
            id: 'sent_count',
            Header: 'Emails Delivered',
            accessor: 'sent_count',
            canSort: true,
            sortType: 'basic',
            sortDirection: sortType.accessor === 'sent_count' ? sortType.sortDirection : 'none',
            Cell: (props: any) => {
              let item = props.row.original;
              return <span style={{ paddingLeft: '50px' }}>{item.total_sent}</span>;
            },
          },
        ],
      },
      {
        disableSortBy: true,
        Header: 'Opens',
        columns: [
          {
            id: 'total_opens',
            Header: 'Total',
            accessor: 'total_opens',
            sortType: 'basic',
            sortDirection: sortType.accessor === 'total_opens' ? sortType.sortDirection : 'none',
            canSort: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return addCommaToNumber((item.total_opens ?? 0)?.toString() ?? '');
            },
          },
          {
            id: 'unique_opens',
            Header: 'Unique',
            accessor: 'unique_opens',
            sortType: 'basic',
            sortDirection: sortType.accessor === 'unique_opens' ? sortType.sortDirection : 'none',
            canSort: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return addCommaToNumber((item.unique_opens ?? 0)?.toString() ?? '');
            },
          },
        ],
      },
      {
        disableSortBy: true,
        Header: 'Clicks',
        columns: [
          {
            id: 'total_clicks',
            Header: 'Total',
            accessor: 'total_clicks',
            sortType: 'basic',
            sortDirection: sortType.accessor === 'total_clicks' ? sortType.sortDirection : 'none',
            canSort: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return addCommaToNumber((item.total_clicks ?? 0)?.toString() ?? '');
            },
          },
          {
            id: 'unique_clicks',
            Header: 'Unique',
            accessor: 'unique_clicks',
            sortType: 'basic',
            sortDirection: sortType.accessor === 'unique_clicks' ? sortType.sortDirection : 'none',
            canSort: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return addCommaToNumber((item.unique_clicks ?? 0)?.toString() ?? '');
            },
          },
        ],
      },
      {
        disableSortBy: true,
        Header: 'Score',
        columns: [
          {
            id: 'campaign_score',
            Header: 'Campaign(s)',
            accessor: 'campaigns_score',
            sortType: 'basic',
            sortDirection: sortType.accessor === 'campaign_score' ? sortType.sortDirection : 'none',
            canSort: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return (
                <div className="last-item">
                  <div className="engagement">
                    <img src={Speed} alt="" />
                    &nbsp;
                    {addCommaToNumber((item.campaigns_score ?? 0)?.toString() ?? '')}
                  </div>
                </div>
              );
            },
          },
          {
            id: 'lifetime_score',
            Header: 'Lifetime',
            accessor: 'lifetime_score',
            disableSortBy: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return (
                <div className="last-item">
                  <div className="engagement">
                    <img src={Speed} alt="" />
                    &nbsp;
                    {addCommaToNumber((item.lifetime_score ?? 0)?.toString() ?? '')}
                  </div>
                </div>
              );
            },
          },
        ],
      },
    ],
    [sortType],
  );

  const sortedResults = (column?: any) => {
    let sort_type = '';
    if (column.canSort) {
      setSortBy(column.id);
      sort_type = setSortDirection(column.id, column.sortDirection);
      setSortType({ accessor: column.id, sortDirection: sort_type });
      setDeliveredContactsSorting(column.id, sort_type);
      store.dispatch({
        type: CONTACT_ANALYTICS.SET_SORT_PROPS,
        payload: { sortBy: column.id, sortType: sort_type },
      });
      store.dispatch({
        type: CONTACT_ANALYTICS.DELIVERED_RESET,
      });
      getDeliveredContactsList(getParams({ sortType: sort_type, sortBy: column.id }), accountId >= 0);
    }
  };

  const getParams = (params?: any) => {
    return getPagination('delivered', {
      allCampaigns: AllCampaignsFilterCheck,
      searchWord: searchedText,
      offset: params !== undefined ? 0 : delivered.length > 0 ? delivered.length : 0,
      sortBy: params !== undefined ? params.sortBy : sortType.accessor,
      sortType: params !== undefined ? params.sortType : sortType.sortDirection,
      ...(contactsByFilter !== '' && { contactsByFilter: contactsByFilter }),
    });
  };

  return (
    <Wrapper delivered={delivered}>
      {delivered !== undefined ? (
        <div className="delivered-contact-list-wrapperClass" style={{ position: 'relative' }}>
          <TableSort
            columns={columnsData}
            data={delivered}
            module={'delivered'}
            _onCellClick={sortedResults}
            _onRowClick={RedirectToContactDetails}
            hasMore={(loading && delivered.length === 0) || delivered?.length < total_delivered}
            // hasMore={true}
            next={() => getDeliveredContactsList(getParams(), accountId >= 0)}
            loader={[1, 2, 3].map((item) => (
              <ContactLoader key={item} loading={loading} />
            ))}
            useCustomQuery
          />
          {!loading && delivered?.length === 0 ? <div className="no-data">No Data Found!</div> : null}
        </div>
      ) : (
        <div className="no-data">No Data Found!</div>
      )}
    </Wrapper>
  );
};

export default DeliveredContactsList;

const Circle = styled.div<{ color: string }>`
  width: 10px;
  height: 10px;
  background: ${(props) => props.color};
  border-radius: 50%;
  margin-right: 3px;
`;

const Wrapper = styled.div<{ delivered?: undefined | [] }>`
  position: relative;
  .infinite-scroll-component__outerdiv {
    height: ${(props) => (props.delivered?.length ?? 0) <= 2 && '100%'};
    /* height: 100%; */
  }
  #scrollableDiv {
    .infinite-scroll-component {
      position: relative;
      min-height: unset !important;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        min-height: unset !important;
      }
    }
  }
  .organization {
    font-size: 16px;
    /* padding-left: 4px; */
    @media all and (min-width: 1920px) and (-webkit-device-pixel-ratio: 1) {
      /* padding-left: 6px; */
    }
    @media all and (max-width: 1440px) {
      /* padding-left: 16px; */
      .ant-typography-ellipsis-single-line {
        width: 90px;
      }
    }
    .ant-typography-ellipsis-single-line {
      width: 90px !important;
      text-align: left;
    }
  }
  .delivered-contact-list-wrapperClass button.ant-btn.ant-btn-default.ant-dropdown-trigger.email-btn {
    width: 62px !important;
    border: 1px solid #dadada !important;
    border-radius: 8px !important;
    padding: 0 !important;
    margin: 0 !important;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 33px !important;
    gap: 4px;
    color: #424b50;
    text-align: center;
    font-family: 'Roboto';
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px; /* 100% */
    letter-spacing: -0.333px;
  }
  .delivered-contact-list-wrapperClass .ant-btn.ant-btn-default.ant-dropdown-trigger.email-btn.ant-dropdown-open {
    border: 1px solid #1ec252 !important;
  }
  .table-sort {
    overflow-x: auto !important;
    overflow-y: hidden !important;
    max-width: 100vw !important;
    width: auto !important;
    @media all and (max-width: 1501px) {
      width: max-content !important;
    }
    @media all and (max-width: 1280px) {
      width: max-content !important;
    }
  }
  width: 100%;
  margin-bottom: 10px;
  /* padding: 14px 13px 14px 18px; */
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 16px;
  @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
    /* border-radius: 7px; */
    /* padding: 10px 12px; */
    min-height: 83px;
    margin-top: 10px;
  }
  > div {
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
  }
  svg {
    rect.personality {
      x: 53%;
      width: 5%;
      @media (max-width: 1621px) {
        x: 33%;
      }
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        x: 41%;
        width: 5%;
      }
    }
    rect.company {
      x: 35%;
      width: 5%;
      @media (max-width: 1621px) {
        x: 21%;
      }
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        x: 23%;
        width: 5%;
      }
    }
    rect.email-delivered {
      x: 42%;
      width: 8%;
      @media (max-width: 1621px) {
        x: 21%;
      }
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        x: 31%;
        width: 7%;
      }
    }
    rect.totalOpen {
      x: 61%;
      width: 3%;
      @media (max-width: 1621px) {
        x: 46%;
      }
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        x: 51%;
        width: 3%;
      }
    }
    rect.uniqueOpen {
      x: 67%;
      width: 3%;
      @media (max-width: 1621px) {
        x: 55%;
      }
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        x: 58%;
        width: 3%;
      }
    }
    rect.totalClick {
      x: 73%;
      width: 3%;
      @media (max-width: 1621px) {
        x: 64%;
      }
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        x: 66%;
        width: 3%;
      }
    }
    rect.uniqueClick {
      x: 79%;
      width: 3%;
      @media (max-width: 1621px) {
        x: 73%;
      }
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        x: 74%;
        width: 3%;
      }
    }

    rect.campaignScore {
      x: 85%;
      width: 5%;
      @media (max-width: 1621px) {
        x: 81%;
      }
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        x: 82%;
        width: 6%;
      }
    }

    rect.lifetimeScore {
      x: 93.5%;
      width: 4%;
      @media (max-width: 1621px) {
        x: 91%;
      }
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        x: 92%;
        width: 4%;
      }
    }
  }

  .no-data {
    position: fixed;
    top: 50%;
    left: 0;
    width: 100%;
    height: 100px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-family: 'robotomedium';
    color: #67696a;
    font-size: 1rem;
    /* margin-bottom: 30px; */
    transform: translateY(50%);
    /* box-shadow: 6px 30px 73px -10px grey; */
    border-radius: 5px;
  }
  > div {
    width: 100%;
  }
  .dropdown-selecter-email {
    background: #fff;
    border-radius: 8px;
    min-height: 36px;
    cursor: pointer;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    /* padding-left: 10px; */
    padding-right: 2px;
    height: 38px;
    width: 118px;
    max-width: 100%;
    position: relative;
    &:focus,
    &:active,
    &.active {
      /* box-shadow: 0 0 0 0.1rem rgb(40 167 69 / 50%); */
    }
    .label {
      color: #7e7e7e;
      font-size: 16px;
      font-weight: 500;
      font-family: 'Roboto', sans-serif;
      flex-wrap: wrap;
      padding: 2px 8px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 12px;
        margin-top: 5px;
      }
      img {
        margin-top: -3px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          width: 15px;
        }
      }
    }
    .seprator {
      -webkit-align-self: stretch;
      -ms-flex-item-align: stretch;
      align-self: stretch;
      background-color: #dddddd;
      margin-bottom: 8px;
      margin-top: 8px;
      width: 1px;
      box-sizing: border-box;
      + .p-2 {
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          padding-top: 3px !important;
          padding-bottom: 3px !important;
        }
      }
    }
  }
  .dropdown-options {
    min-width: 100%;
    width: auto;
    border-radius: 8px;
    transition-duration: 0.5s;
    overflow: hidden;
    height: 0px;
    opacity: 0;
    &.show {
      height: auto;
      opacity: 1;
    }
    .dropdown-options-inner {
      .body-dropdown {
        display: flex;
        justify-content: center;
        align-items: center;
      }
      .message-limit {
        float: left;
        width: 100%;
        padding: 15px;
        text-align: center;
        font-size: 16px;
        font-weight: 500;
        font-family: 'Roboto', sans-serif;
        color: #ffa112;
        background: #ebf5ee;
      }
      .dropdown-header {
        background: #def6e6;
        color: #27ce0a;
        font-weight: 500;
        font-size: 16px;
        line-height: 40px;
        font-family: 'Roboto', sans-serif;
        padding: 10px 16px 0 16px;
        border-radius: 8px 8px 0 0;
        float: left;
        width: 100%;
        svg {
          height: 12px;
          width: 16px;
          margin-right: 12px;
        }
        .sub-header {
          border: 1px solid #dadada;
          padding: 10px;
          background: #f7f7f7;
          float: left;
          margin: 10px -17px 0 -17px;
          width: calc(100% + 34px);
        }
      }
      .dropdown-wrapper {
        float: left;
        width: 100%;
        max-height: 450px;
        overflow: auto;
        &::-webkit-scrollbar {
          display: none;
        }
        &.show-part2 {
          .dropdown-top-filters {
            display: none;
          }
          .dropdown-v2 {
            display: block;
          }
        }
        .dropdown-v2 {
          display: none;
          .header-dropdown {
            background: #f7f7f7;
            border-bottom: 1px solid #dadada;
            padding: 16px 16px 12px 16px;
            float: left;
            width: 100%;
            .back-main {
              cursor: pointer;
              float: left;
              width: 100%;
              img {
                width: 15px;
              }
              .back-label {
                padding-left: 8px;
                color: #424b50;
                font-size: 16px;
                font-weight: 500;
                font-family: 'Roboto', sans-serif;
              }
            }
            .label-dropwon-v2 {
              color: #7e7e7e;
              font-size: 12px;
              font-weight: 500;
              font-family: 'Roboto', sans-serif;
              padding-left: 24px;
              margin-top: -5px;
              float: left;
              display: block;
            }
          }
          .body-dropdown {
            padding: 15px 8px 12px 15px;
            float: left;
            width: 100%;
            .main-label {
              color: #424b50;
              font-size: 14px;
              font-weight: 500;
              font-family: 'Roboto', sans-serif;
              margin-top: 10px;
              float: left;
              width: 100%;
            }
            ul {
              margin: 0;
              padding: 0;
              li {
                padding: 5px 0;
                .ant-radio-wrapper {
                  span {
                    color: #7e7e7e;
                    font-size: 14px;
                    font-weight: 500;
                    font-family: 'Roboto', sans-serif;
                    letter-spacing: -0.35px;
                  }
                }
                input[type='text'] {
                  float: right;
                  width: 65px;
                  border-radius: 13px;
                  border: 1px solid #dadada;
                  color: #7e7e7e;
                  font-size: 16px;
                  font-weight: 500;
                  font-family: 'Roboto', sans-serif;
                  line-height: 16px;
                  padding: 2px 8px;
                  &:focus,
                  &:active,
                  &:focus-visible {
                    outline: none;
                    border: 1px solid #dadada;
                    outline-offset: 0px;
                    border-color: inherit;
                    -webkit-box-shadow: none;
                    box-shadow: none;
                    background: transparent;
                    box-shadow: 0 0 0 0.1rem rgb(40 167 69 / 50%);
                  }
                }
              }
            }
          }
        }
      }
      .dropdown-top-filters {
        /* padding: 16px 16px 16px 16px; */
        /* float: left;
        width: 100%; */
        h4 {
          color: #424b50;
          font-size: 14px;
          font-weight: 500;
          font-family: 'Roboto', sans-serif;
          margin: 0;
          line-height: 40px;
          width: 100%;
          float: left;
        }
        ul {
          width: 100%;
          float: left;
          display: block;
          padding: 0 0 0 20px;
          margin: 0;
          li {
            list-style-type: none;
            margin: 5px 0;
            width: 100%;
            display: block;
            float: left;
            label {
              font-family: 'Roboto', sans-serif;
              color: #7e7e7e;
              font-size: 16px;
              font-weight: 500;
              cursor: pointer;
              display: block;
              padding: 0px 0;
              float: left;
              &:hover {
                color: #27ce0a;
              }
              .ant-checkbox-wrapper {
                display: inline-block;
                float: left;
                &::after {
                  display: none;
                }
              }
              > div {
                /* margin-top: -2px; */
                float: left;
                margin: 8px 12px;
              }
            }
          }
        }
      }
      .dropdown-body {
        padding: 0 16px;
        width: 100%;
        float: left;
        ul {
          width: 100%;
          float: left;
          padding: 20px 20px 20px 0px;
          list-style-type: none;
          li {
            width: 100%;
            float: left;
            padding: 0;
            margin: 0;
            .label {
              font-family: 'Roboto', sans-serif;
              color: #7e7e7e;
              font-size: 14px;
              font-weight: 500;
              cursor: pointer;
              display: block;
              padding: 5px 0;
              line-height: 30px;
              white-space: nowrap;
              text-overflow: ellipsis;
              word-break: keep-all;
              overflow: hidden;
              width: 165px;
              min-width: 165px;
              transition-duration: 0.5s;
            }
            .tick {
              opacity: 0;
              transition-duration: 0.5s;
              margin-right: 13px;
              float: left;
              svg {
                height: 12px;
                width: 16px;
                vertical-align: baseline;
              }
            }
            .current {
              color: #27ce0a;
              opacity: 1;
            }
          }
        }
      }
    }
  }
  table {
    width: 100%;
    margin-top: 10px;
    /* margin-bottom: 20px; */
    thead {
      border-bottom: 1px solid #dddddd;
      tr {
        th {
          color: #5e7079;
          font-weight: 700;
          font-size: 18px;
          font-family: 'Roboto', sans-serif;
        }
      }
    }
    tbody {
      width: 100%;
      display: block;
      tr {
        background-color: #ffffff;
        box-shadow: 0px 0px 17px -2px rgba(0, 0, 0, 0.08);
        width: 100%;
        border-radius: 8px;
        transition-duration: 0.2s;
        position: relative;
        margin: 4px 0;
        padding: 16px 15px 10px 15px;
        cursor: pointer;
        @media all and (max-width: 1439px), (max-height: 730px), (-webkit-device-pixel-ratio: 1.25) {
          padding-right: 0px;
        }
      }
    }
    thead {
      border-bottom: 1px solid #dddddd;
      tr {
        /* padding: 0 15px; */
        &:before {
          max-height: 50px;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            max-height: 38px;
          }
        }
      }
      th {
        color: #5e7079;
        font-weight: 700;
        font-size: 18px;
        font-family: 'Roboto', sans-serif;
        white-space: nowrap;
        span {
          width: 15px;
          .sort_asc {
            width: 10px;
            height: 8px;
            margin-top: -15px;
            margin-left: 5px;
            float: left;
            @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
              margin-top: -13px;
            }
            img {
              transform: rotate(180deg);
            }
          }
          .sort_desc {
            width: 10px;
            height: 8px;
            float: left;
            margin-left: 5px;
            margin-top: 5px;
            img {
              float: left;
            }
          }
        }
      }
      tr:nth-child(1) {
        justify-content: end;
        display: flex;
        th {
          font-size: 18px;
          font-weight: 700;
          color: #5e7079;
          font-family: 'Roboto', sans-serif;
          justify-content: center;
          @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 14px;
          }
          &:nth-child(2) {
            width: 138px;
            @media all and (max-width: 1440px) {
              width: 4%;
              padding-right: 3em;
            }
            @media all and (max-width: 1200px) {
              width: 0%;
              padding-right: 2.5em;
            }
          }
          &:nth-child(3) {
            width: 270px;
            @media all and (max-width: 1440px) {
              width: 11vw;
              padding-left: 132px;
            }
            @media all and (max-width: 1200px) {
              width: 0;
              padding-right: 4.5em;
            }
          }
          &:nth-child(4) {
            width: 240px;
            padding-right: 16px;
            @media all and (max-width: 1440px) {
              width: 23vw;
              padding-left: 120px;
            }
            @media all and (max-width: 1200px) {
              width: 23vw;
              padding-left: 60px;
            }
          }
        }
      }
      tr:nth-child(2) {
        th {
          font-size: 16px;
          color: #5e7079;
          font-weight: 500;
          font-family: 'Roboto', sans-serif;
          white-space: nowrap;
          @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 14px;
          }
          &:nth-child(1) {
            padding-left: 16px;
          }
        }
      }
    }
    tbody {
      width: 100%;
      display: block;
      box-sizing: border-box;
      overflow-y: auto;
      height: calc(100vh - 335px);
      @media all and (min-height: 768px) {
        height: calc(100vh - 335px);
      }
      @media all and (min-width: 1920px) {
        height: calc(100vh - 44vh);
      }

      @media all and (min-width: 1440px) and (max-width: 1610px) {
        height: calc(100vh - 394px);
      }
      @media all and (min-width: 1536px) and (max-width: 1900px) {
        height: calc(100vh - 335px);
      }
      @media all and (min-width: 1600px) and (min-height: 751px) {
        height: calc(100vh - 394px);
      }
      @media all and (max-width: 1080px) {
        height: calc(100vh - 337px);
      }
      scrollbar-width: thin;
      scrollbar-color: #001760 #efefef;
      &::-webkit-scrollbar {
        width: 5px;
        height: 5px;
        border-radius: 5px;
      }
      /* Track */
      &::-webkit-scrollbar-track {
        background: #fff;
        margin-top: 0;
        margin-bottom: 10px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          margin-top: 0;
        }
      }
      /* Handle */
      &::-webkit-scrollbar-thumb {
        background: #001760;
      }
      /* Handle on hover */
      /* &::-webkit-scrollbar-thumb:hover {
        background: #efefef;
      } */
      > div {
        overflow: hidden;
      }
      tr {
        background-color: #ffffff;
        box-shadow: 0px 0px 17px -2px rgba(0, 0, 0, 0.08);
        width: 100%;
        border-radius: 8px;
        transition-duration: 0.2s;
        position: relative;
        margin: 4px 0;
        padding: 16px 15px 10px 15px;
        cursor: pointer;
      }
    }
    thead,
    tbody {
      width: 100%;
      display: block;
      float: left;
      tr:nth-child(1) {
        th {
          &:nth-child(1) {
            @media all and (max-width: 1439px) {
              //Safari (7.1+)
              _::-webkit-full-page-media,
              _:future,
              :root .safari_only {
                width: calc(100% - 535px) !important;
              }
              //Safari (from 6.1 to 10)
              @media screen and (min-color-index: 0) and(-webkit-min-device-pixel-ratio:0) {
                @media {
                  width: calc(100% - 535px) !important;
                }
              }
              //Safari (10.1+)
              @media not all and (min-resolution: 0.001dpcm) {
                @media {
                  width: calc(100% - 535px) !important;
                }
              }
            }
          }
        }
      }
      tr {
        width: 100%;
        display: flex;
        float: left;
        td {
          min-height: 70px;
          font-family: 'Roboto', sans-serif;
          font-weight: 500;
          font-size: 20px !important;
          @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 14px !important;
          }
        }
        th,
        td {
          display: block;
          float: left;
          &[role='cell'] {
            color: #424b50;
          }
          &:nth-child(1) {
            width: 35%;
            text-align: left;
            display: inline-flex;
            align-items: center;
            @media all and (max-width: 1700px) {
              width: 23%;
            }
            /* @media all and (max-width: 1439px) {
              width: calc(100% - 620px);
            } */

            > div {
              display: flex;
              width: 100%;
              align-items: center;
              flex-direction: row;
              min-height: 65px;
              .preview {
                float: left;
              }
              .details {
                float: left;
                font-weight: 400;
                color: #000000;
                font-size: 20px;
                font-family: 'Roboto Slab', serif;
                overflow: hidden;
                @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                  font-size: 14px;
                }
                width: 100%;
                .name {
                  font-weight: 400;
                  color: #000000;
                  font-size: 20px;
                  font-family: 'Roboto Slab', serif;
                  width: 100%;
                  @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                    line-height: 24px;
                  }
                  .ant-typography {
                    margin: 0;
                    max-width: 100%;
                    width: 100%;
                  }
                }
                .company {
                  color: #1ec252;
                  font-weight: 500;
                  font-size: 14px;
                  font-family: 'Roboto', sans-serif;
                  display: block;
                  @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                    font-size: 12px;
                  }
                  .ant-typography {
                    margin: 0;
                    max-width: 100%;
                    color: #1ec252;
                    font-weight: 500;
                    font-size: 14px;
                  }
                }
                .designation {
                  color: #424b50;
                  font-weight: 500;
                  font-size: 14px;
                  font-family: 'Roboto', sans-serif;
                  display: block;
                  @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                    font-size: 12px;
                  }
                  .ant-typography {
                    margin: 0;
                    max-width: 100%;
                  }
                }
              }
            }
          }
          &:nth-child(2) {
            width: 7%;
            text-align: left;
            display: inline-flex;
            align-items: center;
            @media all and (max-width: 1440px) {
              width: 10%;
            }
            @media all and (max-width: 1200px) {
              width: 10%;
            }
            .company-name {
              color: #424b50;
              font-size: 16px;
              @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                font-size: 12px;
              }
              @media all and (max-width: 1125px) {
                padding-left: 12px;
              }
            }
            /* @media all and (max-width: 1621px) {
              width: 10%;
            } */
            /* @media all and (max-width: 1439px) {
              width: 100px;
            } */
          }
          &:nth-child(4) {
            width: 9%;
            text-align: left;
            display: inline-flex;
            align-items: center;
            @media all and (max-width: 1470px) {
              width: 10%;
            }
            @media all and (max-width: 1440px) {
              width: 15%;
            }
            @media all and (max-width: 1200px) {
              width: 15%;
            }
          }
          &:nth-child(3) {
            width: 120px;
            text-align: center;
            display: inline-flex;
            align-items: center;
            /* justify-content: center; */
            /* @media all and (max-width: 1439px) {
              width: 60px;
            } */
          }
          &:nth-child(5) {
            width: 100px;
            text-align: center;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            /* @media all and (max-width: 1439px) {
              width: 60px;
            } */
          }
          &:nth-child(6) {
            width: 100px;
            text-align: center;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            /* @media all and (max-width: 1439px) {
              width: 60px;
            } */
          }
          &:nth-child(7) {
            width: 100px;
            text-align: center;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            /* @media all and (max-width: 1439px) {
              width: 60px;
            } */
          }
          &:nth-child(8) {
            width: 120px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            /* @media all and (max-width: 1439px) {
              width: 100px;
            } */
          }
          &:nth-child(9) {
            width: 120px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            /* @media all and (max-width: 1439px) {
              width: 90px;
            } */
          }
          &:nth-child(10) {
            width: 120px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            /* @media all and (max-width: 1439px) {
              width: 90px;
            } */
          }
        }
      }
    }
  }
  .engagement {
    border-radius: 12px;
    background-color: #1971eb;
    color: #ffffff;
    font-size: 16px;
    font-family: 'robotomedium';
    font-weight: 500;
    padding: 0px 10px;
    display: inline;
    float: right;
    white-space: nowrap;
    @media all and (max-width: 1439px) {
      font-size: 12px;
      padding: 4px 8px;
    }
  }
  .blog-image {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .blog-image {
    .img1 {
      height: 70px;
      width: 52px;
      border-radius: 6px 0px 0px 6px;
      margin-right: 2px;
      @media all and (max-width: 1439px), (max-height: 730px), (-webkit-device-pixel-ratio: 1.25) {
        border-radius: 4px 0px 0px 4px;
        height: 50px;
        width: 36px;
      }
    }
    .img-bg {
      background-position: center center;
      background-size: cover;
      background-repeat: no-repeat;
    }
    .right-images {
      .overlayed-image {
        position: absolute;
        left: 9%;
        top: 22%;
      }
      .img-bg {
        background-position: center center;
        background-size: cover;
        background-repeat: no-repeat;
      }
      display: flex;
      flex-direction: column;
      .img2 {
        height: 34px;
        width: 39px;
        border-radius: 0px 6px 0px 0px;
        margin-bottom: 1px;
        @media all and (max-width: 1439px), (max-height: 730px), (-webkit-device-pixel-ratio: 1.25) {
          border-radius: 0px 4px 0px 0px;
          height: 25px;
          width: 28px;
        }
      }
      .img3 {
        height: 34px;
        width: 39px;
        border-radius: 0px 0px 6px 0px;
        @media all and (max-width: 1439px), (max-height: 730px), (-webkit-device-pixel-ratio: 1.25) {
          border-radius: 0px 0px 4px 0px;
          height: 23px;
          width: 28px;
        }
      }
    }
  }
  .disabled {
    pointer-events: none !important;
    background-color: rgba(225, 225, 225, 0.7) !important;
    cursor: no-drop;
  }
  .preview {
    position: relative;
    max-width: 55px;
    -ms-flex-negative: 1 !important;
    flex-shrink: 1 !important;
    img {
      border-radius: 100%;
      max-width: 100%;
      height: 55px;
      width: 55px;
      min-width: 55px;
      outline: none;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        height: 40px;
        width: 40px;
        min-width: 40px;
      }
    }
  }
  .details {
    padding-left: 15px;
    width: 75%;
    .name,
    .company,
    .designation,
    .recipient-count {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-bottom: 0px;
      -webkit-box-align: start;
      width: 100%;
      word-break: break-word;
    }
    .name {
      color: #000000;
      font-size: 20px;
      font-weight: 400;
      line-height: 25px;
      font-family: 'Roboto Slab', serif;
      display: block;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        line-height: 15px;
        font-size: 13px;
      }
      .ant-typography-ellipsis-single-line {
        width: 100%;
      }
    }
    .list-name {
      color: #000000;
      font-size: 20px;
      font-weight: 400;
      line-height: 22px;
      font-family: 'Roboto Slab', serif;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      word-break: break-word;

      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        line-height: 14px;
        font-size: 13px;
      }
    }
    .company {
      -webkit-line-clamp: 1;
      font-family: 'Roboto', sans-serif;
      font-weight: 500;
      font-size: 14px;
      line-height: 16px;
      color: #424b50;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        line-height: 18px;
        font-size: 9px;
      }
      .ant-typography {
        margin: 0;
        max-width: 100%;
        color: #1ec252;
        font-weight: 500;
        font-size: 14px;
      }
    }
    .designation {
      -webkit-line-clamp: 1;
      font-family: 'Roboto', sans-serif;
      font-weight: 500;
      line-height: 16px;
      font-size: 14px;
      color: #27ce0a;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        line-height: 18px;
        font-size: 9px;
      }
    }
    .recipient-count {
      -webkit-line-clamp: 1;
      font-family: 'Roboto', sans-serif;
      font-weight: 500;
      line-height: 16px;
      font-size: 14px;
      color: #27ce0a;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        line-height: 18px;
        font-size: 9px;
      }
    }
  }
  .actions {
    -ms-flex-negative: 1;
    flex-shrink: 1;
    align-self: center;
    margin-left: auto;
    min-width: 106px;
    ul {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      width: 100%;
      li {
        float: left;
        display: inline-block;
        padding: 0px;
        &.clicks {
          padding-right: 10px;
          .container-span {
            font-family: 'Roboto', sans-serif;
            color: #fff;
            font-size: 16px;
            font-weight: 500;
            background-color: #1971eb;
            border-radius: 12px;
            padding: 0px 10px;
            display: inline-flex;
            width: 80px;
            height: 22px;
            justify-content: center;
            align-items: center;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              font-size: 11px;
              width: 70px;
            }
            svg {
              vertical-align: baseline;
              margin-right: 5px;
            }
          }
        }
        &.card-popover {
          a {
            padding: 0 5px;
            svg {
              width: 5px;
              max-height: none;
              @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                width: 3px;
              }
            }
          }
        }
      }
    }
  }
`;
